import z from "zod";
import { worksites } from "../../attendances";
import { moment } from "../../date";
import { localized } from "../../locales";
import type { Enums } from "../../types";
import { ZodDateString } from "../zod-types/zod-date-string";

/** A time of day as `HH:mm:ss`, whatever precision the time input delivered. */
function toFullTime(time: string) {
	const [hours = "", minutes = "", seconds = ""] = time.split(":");
	return [hours, minutes, seconds].map((part) => part.padStart(2, "0")).join(":");
}

/** A time the form has to carry; an emptied time input sends `null`, which reads the same. */
const requiredTime = z
	.string({ error: () => localized("general.is_required") })
	.min(1, { error: () => localized("general.is_required") });

const addAttendanceShape = {
	date: ZodDateString,
	startTime: requiredTime,
	endTime: requiredTime,
	worksite: z.enum(worksites),
	projectId: z.uuid().nullish(),
};

interface AddAttendanceValues {
	date: string;
	startTime: string;
	endTime: string;
	worksite: Enums<"attendanceWorksiteType">;
	projectId?: string | null;
}

/**
 * Both times as `HH:mm:ss`, and a project only where one belongs — on a project site, or on a
 * commute headed for one.
 */
function normalize<T extends AddAttendanceValues>(values: T): T {
	const hasProject = values.worksite === "project-site" || values.worksite === "commute";
	return {
		...values,
		startTime: toFullTime(values.startTime),
		endTime: toFullTime(values.endTime),
		projectId: hasProject ? (values.projectId ?? null) : null,
	};
}

/**
 * The entry has to end after it starts and must not end in the future: it records time that was
 * worked, not time somebody is working now. A project site names its project.
 */
function refine(values: AddAttendanceValues, context: z.core.$RefinementCtx) {
	const start = moment(values.date).setTime(values.startTime);
	const end = moment(values.date).setTime(values.endTime);
	if (!end.isAfter(start)) {
		context.addIssue({
			code: "custom",
			path: ["endTime"],
			message: localized("users.attendance_end_before_start"),
		});
	}
	if (end.isAfter(moment())) {
		context.addIssue({
			code: "custom",
			path: ["endTime"],
			message: localized("users.attendance_in_the_future"),
		});
	}
	if (values.worksite === "project-site" && !values.projectId) {
		context.addIssue({
			code: "custom",
			path: ["projectId"],
			message: localized("projects.select_project"),
		});
	}
}

/**
 * An attendance added to somebody's sheet after the fact — the day they forgot to check in: the
 * day, the span within it, and where the work was done. It is the body of
 * `POST /users/:userId/attendances`, whose route names the person.
 *
 * A schema with a transform and a refinement cannot be extended, so the form that also picks the
 * person is a second schema over the same fields rather than one derived from this.
 */
export const addAttendanceFormSchema = z
	.object(addAttendanceShape)
	.transform(normalize)
	.superRefine(refine);

/** The same entry and the person it is for, for the form that lets the caller pick them. */
export const addAttendanceForUserFormSchema = z
	.object({
		...addAttendanceShape,
		userId: z.string().min(1, { error: () => localized("users.select_user") }),
	})
	.transform(normalize)
	.superRefine(refine);

export type AddAttendanceFormSchema = z.infer<typeof addAttendanceFormSchema>;
export type AddAttendanceForUserFormSchema = z.infer<typeof addAttendanceForUserFormSchema>;
